/**
 * What: Renders treatment content with support for image thumbnails.
 * Transport & data-flow: Pure presentation layer. Formats raw text labels and resolves image URLs to Polaris Thumbnails.
 *
 */

import { productLanguage, shopifyComponentCopy } from '@testfeed/product-language'
import { isCanonicalShopifyImageVariantUrl } from '../../../src/study-brief/image-treatment'

/** Detects whether treatment content is an image URL. */
export function isImageUrl(value: string | null | undefined): boolean {
  if (!value) return false
  const trimmed = value.trim()

  if (isCanonicalShopifyImageVariantUrl(trimmed)) return true
  if (trimmed.includes('/media/uploads/media/') || trimmed.includes('/api/uploads/uploads/media/')) return true
  if (trimmed.includes('cdn.shopify.com/')) return true

  try {
    const url = new URL(trimmed)
    const hasImageExtension = /\.(jpeg|jpg|gif|png|svg|webp|bmp)(?:\?.*)?$/i.test(url.pathname)
    return (url.protocol === 'https:' || url.protocol === 'http:') && hasImageExtension
  } catch (error) {
    console.warn('Failed to parse image URL in isImageUrl:', error)
    return /^(https?:)?\/\/[^\s?]+?\.(?:png|jpg|jpeg|gif|webp|svg|bmp)(?:\?[^\s]*)?$/i.test(trimmed)
  }
}

/** Extracts a clean, human-readable file name from an image URL. */
export function formatImageLabel(value: string): string {
  try {
    const url = new URL(value)
    const pathname = url.pathname
    const parts = pathname.split('/')
    const lastPart = parts[parts.length - 1]
    if (lastPart) {
      return decodeURIComponent(lastPart)
    }
  } catch (error) {
    console.warn('Failed to parse image URL in formatImageLabel:', error)
  }
  return value
}

interface ArmLabelDisplayProps {
  label: string | null
  alt?: string
  size?: 'extraSmall' | 'small' | 'medium'
}

/** s-thumbnail's size scale doesn't match Thumbnail's — closest equivalent per step. */
const sThumbnailSizeBySize = {
  extraSmall: 'small-200',
  small: 'small',
  medium: 'base',
} as const

/**
 * Renders a stable treatment identity. If the value is an image, show only the
 * thumbnail; authored treatment descriptions are rendered by the surrounding
 * result surface so this component does not turn asset URLs into merchant copy.
 */
export function ArmLabelDisplay({ label, alt, size = 'small' }: ArmLabelDisplayProps) {
  if (!label) {
    return (
      <s-thumbnail size={sThumbnailSizeBySize[size]} alt={shopifyComponentCopy.resultsTreatmentCard.noProductImage} />
    )
  }

  if (!isImageUrl(label)) {
    return <s-text>{label}</s-text>
  }

  return (
    <s-thumbnail
      src={label}
      size={sThumbnailSizeBySize[size]}
      alt={alt ?? productLanguage.studyResults.tables.treatmentImageAlt}
    />
  )
}
